import React, { useState } from "react";
import { Calendar, Download, Clock, TrendingUp, Users, Zap, Plus, X, Mail, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Report Types
const REPORT_TYPES = [
  { id: "daily-ops", name: "Daily Operations Report", description: "Daily summary of truck movements and processing times", frequency: "Daily" },
  { id: "weekly-summary", name: "Weekly Summary Report", description: "Weekly performance metrics and trends", frequency: "Weekly" },
  { id: "monthly-perf", name: "Monthly Performance Report", description: "Comprehensive monthly analysis and KPIs", frequency: "Monthly" },
  { id: "visitor-access", name: "Visitor Access Report", description: "Visitor entry and access log summary", frequency: "Daily" },
  { id: "stock-movement", name: "Stock Movement Report", description: "Inbound/outbound inventory movements", frequency: "Daily" },
  { id: "capacity-utilization", name: "Capacity Utilization Report", description: "Site capacity usage and optimization", frequency: "Weekly" },
  { id: "financial", name: "Financial Report", description: "Billing and revenue summary", frequency: "Monthly" },
];

const AllReports = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [selectedReportType, setSelectedReportType] = useState("");
  const [cronSchedule, setCronSchedule] = useState("0 8 * * *");
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [schedules, setSchedules] = useState<any[]>([
    { id: 1, name: "Daily Operations", reportType: "daily-ops", cron: "0 8 * * *", emails: ["admin@navig8.com"], active: true, lastSent: "2025-11-27 08:00" },
    { id: 2, name: "Weekly Performance", reportType: "weekly-summary", cron: "0 9 * * 1", emails: ["manager@navig8.com"], active: true, lastSent: "2025-11-24 09:00" },
  ]);

  const handleAddEmail = () => {
    if (newEmail && !emailRecipients.includes(newEmail)) {
      setEmailRecipients([...emailRecipients, newEmail]);
      setNewEmail("");
    }
  };

  const handleRemoveEmail = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  const handleCreateSchedule = () => {
    if (!scheduleName || !selectedReportType || emailRecipients.length === 0) {
      alert("Please fill in all fields and add at least one email recipient");
      return;
    }

    const newSchedule = {
      id: schedules.length + 1,
      name: scheduleName,
      reportType: selectedReportType,
      cron: cronSchedule,
      emails: emailRecipients,
      active: isActive,
      lastSent: new Date().toLocaleString(),
    };

    setSchedules([...schedules, newSchedule]);
    setScheduleDialogOpen(false);
    setScheduleName("");
    setSelectedReportType("");
    setCronSchedule("0 8 * * *");
    setEmailRecipients([]);
    setNewEmail("");
    setIsActive(true);
    alert("Report schedule created successfully!");
  };

  const handleDeleteSchedule = (id: number) => {
    setSchedules(schedules.filter(s => s.id !== id));
  };

  const getReportTypeName = (typeId: string) => {
    return REPORT_TYPES.find(t => t.id === typeId)?.name || typeId;
  };

  const getCronDescription = (cron: string) => {
    if (cron === "0 8 * * *") return "Daily at 8:00 AM";
    if (cron === "0 9 * * 1") return "Every Monday at 9:00 AM";
    if (cron === "0 9 1 * *") return "First day of month at 9:00 AM";
    return cron;
  };

  // Mock data for charts
  const dailyTrendsData = [
    { name: "Mon", visits: 2.8 },
    { name: "Tue", visits: 2.2 },
    { name: "Wed", visits: 1.8 },
    { name: "Thu", visits: 2.5 },
    { name: "Fri", visits: 2.3 },
    { name: "Sat", visits: 2.9 },
    { name: "Sun", visits: 2.1 },
  ];

  const shiftData = [
    { shift: "Morning Shift", value: 3 },
    { shift: "Afternoon Shift", value: 0 },
    { shift: "Night Shift", value: 0 },
  ];

  const turnaroundData = [
    { hour: "00:00", avgTurnaround: 45 },
    { hour: "04:00", avgTurnaround: 38 },
    { hour: "08:00", avgTurnaround: 52 },
    { hour: "12:00", avgTurnaround: 48 },
    { hour: "16:00", avgTurnaround: 55 },
    { hour: "20:00", avgTurnaround: 42 },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]">Reports & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Comprehensive reporting and insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setScheduleDialogOpen(true)}
            className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
            data-testid="button-schedule-reports"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Schedule Reports
          </Button>
          <Button size="sm" className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Report Filters */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader>
          <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2 text-base">
            <TrendingUp className="w-4 h-4" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Start Date</label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(187,100%,42%)]" />
                <Input
                  type="date"
                  placeholder="Pick a date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">End Date</label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(187,100%,42%)]" />
                <Input
                  type="date"
                  placeholder="Pick a date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)] p-1 grid w-full grid-cols-5 h-auto">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] flex items-center gap-2 py-2"
          >
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger
            value="visitors"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] flex items-center gap-2 py-2"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Visitors</span>
          </TabsTrigger>
          <TabsTrigger
            value="allowance"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] flex items-center gap-2 py-2"
          >
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">Allowance</span>
          </TabsTrigger>
          <TabsTrigger
            value="cycle-times"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] flex items-center gap-2 py-2"
          >
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Cycle Times</span>
          </TabsTrigger>
          <TabsTrigger
            value="turnaround"
            className="data-[state=active]:bg-[hsl(187,100%,42%)] data-[state=active]:text-black text-[hsl(215,20%,65%)] flex items-center gap-2 py-2"
          >
            <TrendingUp className="w-4 h-4" />
            <span className="hidden sm:inline">Turnaround</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Total Visits</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">3</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Completed</span>
                  <span className="text-3xl font-bold text-green-500">0</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Active</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">3</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Active Sessions</span>
                  <span className="text-3xl font-bold text-red-500">0</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Daily Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dailyTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                    <XAxis stroke="hsl(215,20%,45%)" />
                    <YAxis stroke="hsl(215,20%,45%)" />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                    <Line type="monotone" dataKey="visits" stroke="hsl(187,100%,42%)" dot={{ fill: "hsl(187,100%,42%)" }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Top Transporters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <p>No transporter data available</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Visitors Tab */}
        <TabsContent value="visitors" className="space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Visitor Filters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-2">Transporter Name</label>
                  <Input
                    placeholder="Search transporter..."
                    className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-2">Plate Number</label>
                  <Input
                    placeholder="Search plate..."
                    className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-2">Driver Name</label>
                  <Input
                    placeholder="Search driver..."
                    className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visitor Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Total Visits</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">3</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Completed</span>
                  <span className="text-3xl font-bold text-green-500">0</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Active</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">3</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Visit Details</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">3 visits found</p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Visit details will display here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Allowance Tab */}
        <TabsContent value="allowance" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Total Clients</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">0</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Total Trucks</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">0</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Cycle Times Tab */}
        <TabsContent value="cycle-times" className="space-y-6">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">Shift Summary</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Total Trucks: 3 | Busiest Shift: Morning Shift</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={shiftData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(215,28%,18%)" />
                  <XAxis stroke="hsl(215,20%,45%)" />
                  <YAxis stroke="hsl(215,20%,45%)" />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(215,28%,14%)", border: "1px solid hsl(215,28%,18%)" }} />
                  <Bar dataKey="value" fill="hsl(200,100%,60%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-sm">Morning Shift</CardTitle>
                <p className="text-xs text-muted-foreground">06:00 - 15:00</p>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <span className="text-xs text-muted-foreground">Total Trucks:</span>
                  <p className="text-2xl font-bold text-[hsl(187,100%,42%)]">3</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Avg per Day:</span>
                  <p className="text-2xl font-bold text-[hsl(187,100%,42%)]">3.0</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-sm">Afternoon Shift</CardTitle>
                <p className="text-xs text-muted-foreground">15:00 - 23:00</p>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <span className="text-xs text-muted-foreground">Total Trucks:</span>
                  <p className="text-2xl font-bold text-green-500">0</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Avg per Day:</span>
                  <p className="text-2xl font-bold text-green-500">0</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-sm">Night Shift</CardTitle>
                <p className="text-xs text-muted-foreground">23:00 - 06:00</p>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <span className="text-xs text-muted-foreground">Total Trucks:</span>
                  <p className="text-2xl font-bold text-green-500">0</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Avg per Day:</span>
                  <p className="text-2xl font-bold text-green-500">0</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Turnaround Tab */}
        <TabsContent value="turnaround" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Trucks On Site</span>
                  <span className="text-3xl font-bold text-[hsl(187,100%,42%)]">3</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Avg Turnaround</span>
                  <span className="text-3xl font-bold text-green-500">0 min</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Min Turnaround</span>
                  <span className="text-3xl font-bold text-green-500">0 min</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardContent className="pt-6">
                <div className="flex flex-col">
                  <span className="text-xs text-muted-foreground mb-1">Max Turnaround</span>
                  <span className="text-3xl font-bold text-red-500">0 min</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Average per Day</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <p>No data available</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] text-base">Average per Hour</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <p>No data available</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Scheduled Reports Section */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader>
          <CardTitle className="text-[hsl(187,100%,42%)] flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Active Report Schedules
            </span>
            <Badge variant="secondary" className="bg-[hsl(187,100%,42%)]/20 text-[hsl(187,100%,42%)]">
              {schedules.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No report schedules configured yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {schedules.map(schedule => (
                <div key={schedule.id} className="p-4 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)] flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-medium text-white">{schedule.name}</h4>
                      <Badge className={schedule.active ? "bg-green-500/20 text-green-500" : "bg-gray-500/20 text-gray-500"}>
                        {schedule.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{getReportTypeName(schedule.reportType)}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getCronDescription(schedule.cron)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {schedule.emails.length} recipient{schedule.emails.length !== 1 ? 's' : ''}
                      </span>
                      {schedule.lastSent && (
                        <span className="text-muted-foreground/70">Last sent: {schedule.lastSent}</span>
                      )}
                    </div>
                  </div>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => handleDeleteSchedule(schedule.id)}
                    className="text-red-500 hover:bg-red-500/20"
                    data-testid={`button-delete-schedule-${schedule.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-lg bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">Create Report Schedule</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Set up automated PDF report delivery to your team
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Schedule Name */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Schedule Name</label>
              <Input
                placeholder="e.g., Daily Operations Report"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                data-testid="input-schedule-name"
              />
            </div>

            {/* Report Type */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Report Type</label>
              <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex flex-col">
                        <span>{type.name}</span>
                        <span className="text-xs text-muted-foreground">{type.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Cron Schedule */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Schedule (Cron Format)</label>
              <Input
                placeholder="0 8 * * *"
                value={cronSchedule}
                onChange={(e) => setCronSchedule(e.target.value)}
                className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                data-testid="input-cron-schedule"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: <strong>{getCronDescription(cronSchedule)}</strong>
              </p>
            </div>

            {/* Email Recipients */}
            <div>
              <label className="text-sm text-[hsl(187,100%,42%)] block mb-2">Email Recipients</label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
                  className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)] text-white placeholder:text-[hsl(215,20%,45%)]"
                  data-testid="input-email"
                />
                <Button 
                  onClick={handleAddEmail}
                  className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black"
                  size="icon"
                  data-testid="button-add-email"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {emailRecipients.length > 0 && (
                <div className="space-y-2">
                  {emailRecipients.map(email => (
                    <div key={email} className="flex items-center justify-between p-2 bg-[hsl(215,28%,12%)] rounded border border-[hsl(215,28%,18%)]">
                      <span className="text-sm text-white">{email}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleRemoveEmail(email)}
                        className="w-6 h-6 text-red-500 hover:bg-red-500/20"
                        data-testid={`button-remove-email-${email}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-[hsl(215,28%,12%)] rounded border border-[hsl(215,28%,18%)]">
              <label className="text-sm text-[hsl(187,100%,42%)]">Active Schedule</label>
              <button
                onClick={() => setIsActive(!isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isActive ? "bg-[hsl(24.6,95%,53%)]" : "bg-gray-600"
                }`}
                data-testid="toggle-active-schedule"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setScheduleDialogOpen(false)}
              className="border-[hsl(215,28%,25%)] text-[hsl(215,20%,65%)]"
              data-testid="button-cancel-schedule"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSchedule}
              className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
              data-testid="button-create-schedule"
            >
              Create Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllReports;
